// YouTube mode — client-side sorting/filtering of YtListItem[] (clone of
// ../api/sort.ts, which is typed on the cinematic ListItem and sorts by
// year/rating; videos sort by channel, publish date, duration, and watches).

import { YT_RATING_LABELS, type YtListItem } from "./api";

export type YtSortField = "title" | "channel" | "published" | "duration" | "watches" | "rating";
export type YtSortDir = "asc" | "desc";
export interface YtSortState {
  field: YtSortField;
  dir: YtSortDir;
}

export const YT_SORT_FIELDS: YtSortField[] = [
  "title",
  "channel",
  "published",
  "duration",
  "watches",
  "rating",
];
export const YT_SORT_FIELD_LABEL: Record<YtSortField, string> = {
  title: "A–Z",
  channel: "channel",
  published: "published",
  duration: "duration",
  watches: "watches",
  rating: "rating",
};

const STORAGE_KEY = "cinedb.youtube.sort";
const DEFAULT: YtSortState = { field: "title", dir: "asc" };

export function loadYtSort(): YtSortState {
  if (typeof window === "undefined") return DEFAULT;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const p = JSON.parse(raw) as YtSortState;
      if (YT_SORT_FIELDS.includes(p.field) && (p.dir === "asc" || p.dir === "desc")) return p;
    }
  } catch {
    /* fall through to default */
  }
  return DEFAULT;
}

export function saveYtSort(s: YtSortState): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  } catch {
    /* ignore */
  }
}

export function cycleYtField(field: YtSortField): YtSortField {
  return YT_SORT_FIELDS[(YT_SORT_FIELDS.indexOf(field) + 1) % YT_SORT_FIELDS.length];
}

const ARTICLE = /^(the|a|an)\s+/i;
const titleKey = (t: string): string => t.replace(ARTICLE, "").toLowerCase();

// ── Filtering — options are contextual to the active sort field ──────────────
export interface YtFilterState {
  field: YtSortField;
  value: string;
}
export interface YtFilterOption {
  value: string;
  label: string;
}

const firstLetter = (t: string): string => {
  const c = t.replace(ARTICLE, "").trimStart().charAt(0).toUpperCase();
  return c >= "A" && c <= "Z" ? c : "#";
};

const LETTERS: YtFilterOption[] = [
  ..."ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("").map((c) => ({ value: c, label: c })),
  { value: "#", label: "# (other)" },
];

// Duration buckets: value = lower bound in minutes.
const DURATION_BUCKETS: YtFilterOption[] = [
  { value: "0", label: "< 5 min" },
  { value: "5", label: "5–20 min" },
  { value: "20", label: "20–60 min" },
  { value: "60", label: "1–3 h" },
  { value: "180", label: "> 3 h" },
];

const WATCH_BUCKETS: YtFilterOption[] = [
  { value: "1", label: "watched once" },
  { value: "2", label: "twice" },
  { value: "3", label: "3–5 times" },
  { value: "6", label: "6+ times" },
];

function durationBucket(seconds: number | null): string | null {
  if (seconds == null) return null;
  const m = seconds / 60;
  if (m < 5) return "0";
  if (m < 20) return "5";
  if (m < 60) return "20";
  if (m < 180) return "60";
  return "180";
}

function watchBucket(n: number): string {
  if (n <= 1) return "1";
  if (n === 2) return "2";
  if (n <= 5) return "3";
  return "6";
}

/** The dropdown choices for the current sort field. */
export function ytFilterOptions(field: YtSortField): YtFilterOption[] {
  switch (field) {
    case "title":
    case "channel":
      return LETTERS;
    case "published": {
      const top = new Date().getFullYear();
      const opts: YtFilterOption[] = [];
      for (let y = top; y >= 2005; y -= 1) opts.push({ value: String(y), label: String(y) });
      return opts;
    }
    case "duration":
      return DURATION_BUCKETS;
    case "watches":
      return WATCH_BUCKETS;
    case "rating":
      return [1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => ({
        value: String(n),
        label: `${n} · ${YT_RATING_LABELS[n]}`,
      }));
  }
}

export function ytFilterLabel(filter: YtFilterState): string {
  return (
    ytFilterOptions(filter.field).find((o) => o.value === filter.value)?.label ?? filter.value
  );
}

function matchesFilter(item: YtListItem, f: YtFilterState): boolean {
  switch (f.field) {
    case "title":
      return firstLetter(item.title) === f.value;
    case "channel":
      return item.channel != null && firstLetter(item.channel) === f.value;
    case "published":
      return item.published_at != null && item.published_at.slice(0, 4) === f.value;
    case "duration":
      return durationBucket(item.duration_seconds) === f.value;
    case "watches":
      return watchBucket(item.watch_count) === f.value;
    case "rating":
      return item.rating != null && item.rating === Number(f.value);
  }
}

/** Filter (if set) then sort — the single order every consumer renders. */
export function ytViewItems(
  items: YtListItem[],
  sort: YtSortState,
  filter: YtFilterState | null,
): YtListItem[] {
  const base = filter ? items.filter((i) => matchesFilter(i, filter)) : items;
  return ytSortItems(base, sort);
}

function numericKey(item: YtListItem, field: YtSortField): number | null {
  switch (field) {
    case "duration":
      return item.duration_seconds;
    case "watches":
      return item.watch_count;
    case "rating":
      return item.rating;
    case "published":
      return item.published_at ? Date.parse(item.published_at) || null : null;
    default:
      return null;
  }
}

/** A stably-sorted copy of `items`. Missing values always sort last. */
export function ytSortItems(items: YtListItem[], { field, dir }: YtSortState): YtListItem[] {
  const mult = dir === "asc" ? 1 : -1;
  const byTitle = (a: YtListItem, b: YtListItem) =>
    titleKey(a.title).localeCompare(titleKey(b.title));
  return [...items].sort((a, b) => {
    if (field === "title") return byTitle(a, b) * mult;
    if (field === "channel") {
      const ac = a.channel?.toLowerCase() ?? null;
      const bc = b.channel?.toLowerCase() ?? null;
      if (ac == null && bc == null) return byTitle(a, b);
      if (ac == null) return 1;
      if (bc == null) return -1;
      const c = ac.localeCompare(bc);
      return c !== 0 ? c * mult : byTitle(a, b);
    }
    const av = numericKey(a, field);
    const bv = numericKey(b, field);
    if (av == null && bv == null) return byTitle(a, b);
    if (av == null) return 1; // nulls last, regardless of direction
    if (bv == null) return -1;
    if (av !== bv) return (av - bv) * mult;
    return byTitle(a, b); // tie-break alphabetically
  });
}
